import { DevTool } from '@hookform/devtools'
import { yupResolver } from '@hookform/resolvers/yup'
import { useMutation } from '@tanstack/react-query'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import * as Yup from 'yup'

import type { ReactElement } from 'react'

import { createFeedback } from '~/api/feedbacks'
import Button from '~/components/Buttons/Button'
import InputGroup from '~/components/Inputs/InputGroup'
import FileUploadInput from '~/components/Inputs/ReactHookForm/FileUploadInput'
import StarRatingInput from '~/components/Inputs/ReactHookForm/StarRatingInput'
import TextArea from '~/components/Inputs/ReactHookForm/TextArea'
import TextInput from '~/components/Inputs/ReactHookForm/TextInput'
import { ModalBody, ModalFooter, ModalHeader } from '~/components/Modal/Layout'
import { FORM_ERRORS } from '~/const'
import { NOTION_HOST } from '~/const/notion'
import { useGetMeAsUserQuery } from '~/queries/users'
import dayjs from '~/utils/dayjs'

interface FormValues {
  message: string
  rating: number
  screenshots: string[]
  title: string
}

const validationSchema = Yup.object().shape({
  message: Yup.string().trim().required(FORM_ERRORS.REQUIRED),
  rating: Yup.number().required(FORM_ERRORS.REQUIRED),
  screenshots: Yup.array().of(Yup.string().required()).required(),
  title: Yup.string().trim().required(FORM_ERRORS.REQUIRED),
})

interface Props {
  onClose: VoidFunction
  onFailure: (error: unknown) => void
  onSuccess: (feedbackUrl: string) => void
}

const CreateModal = ({ onClose, onFailure, onSuccess }: Props): ReactElement => {
  /** Local state */

  const { data: user } = useGetMeAsUserQuery(true)

  const defaultTitle = `${dayjs().format('YYMMDD')}_${user?.first_name.toLowerCase()}-${user?.last_name.toLowerCase()}`

  const defaultValues: FormValues = {
    message: '',
    rating: 0,
    screenshots: [],
    title: defaultTitle,
  }

  const methods = useForm<FormValues>({
    defaultValues,
    resolver: yupResolver(validationSchema),
  })

  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
    register,
  } = methods

  /** Queries & Mutations */

  const { mutateAsync: createFeedbackMutation } = useMutation({ mutationFn: createFeedback })

  /** Handlers */

  const handleFormSubmit = async (values: FormValues) => {
    try {
      const { id } = await createFeedbackMutation({
        ...values,
        rating: values.rating,
        screenSize: `w: ${window.innerWidth}/h: ${window.innerHeight}`,
        screenshotUrl: values.screenshots[0] ? `${location.host}/file/${values.screenshots[0]}` : undefined,
        title: values.title || defaultTitle,
        url: window.location.href,
        userAgent: window.navigator.userAgent,
      })
      onSuccess(`${NOTION_HOST}/${values.title}-${id.replace(/-/g, '')}`)
    } catch (e) {
      onFailure(e)
    }
  }

  /** Render */

  return (
    <div className="create-feedback-modal">
      <ModalHeader>Give your feedback</ModalHeader>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <ModalBody>
            <InputGroup>
              <StarRatingInput control={control} error={errors.rating?.message} name="rating" />
              <TextInput
                {...register('title')}
                error={errors.title?.message}
                label="Title"
                placeholder="Enter a title"
                required
              />
              <TextArea
                {...register('message')}
                error={errors.message?.message}
                label="Message"
                placeholder="Enter your feedback (bug, remark, suggestion...)"
                required
              />
              <Controller
                control={control}
                name="screenshots"
                render={({ field }) => (
                  <FileUploadInput
                    {...field}
                    accept={{ 'image/*': [] }}
                    error={errors.screenshots?.message}
                    label="Screenshot"
                    multiple={false}
                  />
                )}
              />
            </InputGroup>
          </ModalBody>
          <ModalFooter>
            <Button disabled={isSubmitting} ghost onClick={onClose}>
              Cancel
            </Button>
            <Button disabled={isSubmitting} type="submit">
              {isSubmitting ? 'Loading...' : 'Save'}
            </Button>
          </ModalFooter>
          <DevTool control={control} />
        </form>
      </FormProvider>
    </div>
  )
}

export default CreateModal
